import React, { useContext, useRef, useMemo, useState } from 'react';
import classes from './SlateToolbar.module.scss';
import { Button } from '../../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../../constants/button-colors';
import { IconsEnum } from '../../../../constants/icons';
import { CustomEditor } from '../../../../models/slate-editor';
import { EmojiPicker } from '../EmojiPicker';
import { BaseEmoji } from 'emoji-mart';
import { ReactEditor } from 'slate-react';
import { Editor, Transforms } from 'slate';
import { SnippetPicker } from '../SnippetPicker';
import { CreateEditSnippetModal } from '../../../Publish/Snippets/CreateEditSnippetModal';
import { CharacterCounter } from '../CharacterCounter';
import { HashtagCounter } from '../HashtagCounter';
import { useBreakpoints } from '../../../../hooks/useBreakpoints';
import classNames from 'classnames';
import { Tooltip } from 'components/actions/Tooltip';
import { PostEditorGroupDataContext } from '../../states/PostEditorGroupDataProvider';
import { ActiveCustomizeIndexContext } from 'modules/PostEditor/states/ActiveCustomizeIndexProvider';
import { formatErrorMessages } from '../../validators/helpers/formatErrorMessages';
import { HASHTAG_SEARCH_URL } from '../../../../constants/hashtag-search';
import { OutsideClickWrapper } from '../../../../components/actions/OutsideClickWrapper';
import { idGenerator } from 'helpers/idGenerator';
import { DropdownWrapper } from '../../../../components/containers/DropdownWrapper';
import scssVars from '../../../../assets/scss/exportedVars.module.scss';

export type SlateToolbarViewProps = {
  //
  addComment?: VoidFunction;
  addVariation?: VoidFunction;
  aiAssistantTooltip?: JSX.Element | string;
  canDelete: boolean;
  comboErrorMessages: string[];
  comboWarningMessages: string[];
  editor: CustomEditor;
  emojiTooltip?: JSX.Element | string;
  errorVariationMessages: string[];
  hashtagsCount?: number;
  openAiAssistant?: VoidFunction;
  publishNext: boolean;
  snippetsTooltip?: JSX.Element | string;
  variationId?: number;
  warningVariationMessages: string[];
};

const openHashtag = () => {
  window.open(HASHTAG_SEARCH_URL, '_blank');
};

export const SlateToolbarView = (props: SlateToolbarViewProps): JSX.Element => {
  const {
    addComment,
    addVariation,
    aiAssistantTooltip,
    canDelete,
    comboErrorMessages,
    comboWarningMessages,
    editor,
    emojiTooltip,
    errorVariationMessages,
    hashtagsCount,
    openAiAssistant,
    publishNext,
    snippetsTooltip,
    variationId,
    warningVariationMessages
  } = props;
  const [showSnippet, setShowSnippet] = useState<boolean>(false);
  const [showCreateSnippet, setShowCreateSnippet] = useState<boolean>(false);
  const [showEmoji, setShowEmoji] = useState<boolean>(false);
  const breakpoint = useBreakpoints();
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;
  const customizeIndex = useContext(ActiveCustomizeIndexContext);
  const idSuffix = useMemo(() => idGenerator(), []);

  const buttonClasses = classNames(
    'grid-x align-middle',
    !!addVariation || !!addComment ? 'align-justify' : 'align-right'
  );
  const emojiWrapperRef = useRef<HTMLDivElement | null>(null);

  const toggleSnippets = () => {
    setShowSnippet(old => !old);
  };
  const addEmoji = (emoji: BaseEmoji) => {
    ReactEditor.focus(editor);
    const { selection } = editor;
    if (!selection) {
      Transforms.select(editor, Editor.end(editor, []));
    }
    editor.insertText(emoji.native);
    setShowEmoji(false);
  };

  const closeEmojiHandler = () => setShowEmoji(false);

  return (
    <div className={classNames(classes.SlateToolbar, canDelete ? classes.SlateToolbarCanDelete : undefined)}>
      <div className={buttonClasses}>
        {!!(addVariation || addComment) && (
          <div className={classNames('cell', 'shrink', classes.VariationButtonOffset)}>
            <div className={'grid-x'}>
              {addVariation && (
                <div className={'cell shrink'}>
                  <Button
                    action={addVariation}
                    type={'button'}
                    variant={'clear'}
                    size={'medium'}
                    color={ButtonColorsEnum.primary}
                    icon={{
                      side: breakpoint === 'small' || breakpoint === 'tabletportrait' ? 'center' : 'left',
                      value: IconsEnum.duplicate
                    }}>
                    {breakpoint === 'small' || breakpoint === 'tabletportrait' ? '' : 'Add Variation'}
                  </Button>
                </div>
              )}
              {addComment && (
                <div className={'cell shrink'}>
                  <Button
                    action={addComment}
                    type={'button'}
                    variant={'clear'}
                    size={'medium'}
                    color={ButtonColorsEnum.primary}
                    icon={{
                      side:
                        breakpoint === 'small' || breakpoint === 'tabletportrait' || breakpoint === 'large'
                          ? 'center'
                          : 'left',
                      value: IconsEnum.comments
                    }}>
                    {breakpoint === 'small' || breakpoint === 'tabletportrait' || breakpoint === 'large'
                      ? ''
                      : 'Add First Comment'}
                  </Button>
                </div>
              )}
              {publishNext && (breakpoint === 'small' || breakpoint === 'tabletportrait') ? (
                <div className={'cell shrink'}>
                  <div className={classes.PublishNext}>next</div>
                </div>
              ) : null}
              {comboWarningMessages.length ? (
                <div className={'cell shrink align-self-middle'}>
                  <Tooltip
                    icon={'warning'}
                    target={`#tooltipTargetTextBlockWarning_${variationId}_${
                      customizeIndex !== undefined ? customizeIndex : 'group'
                    }_${postGroupIndex}`}
                    position={'right'}
                    pointer={true}
                    type={'inlined'}
                    autoZIndex={false}
                    style={{ zIndex: scssVars['z-index-overmodal'] }}>
                    {formatErrorMessages(comboWarningMessages)}
                  </Tooltip>
                </div>
              ) : null}
              {comboErrorMessages.length ? (
                <div className={'cell shrink align-self-middle'}>
                  <Tooltip
                    icon={'alert'}
                    target={`#tooltipTargetTextBlockErrorCombo_${variationId}_${
                      customizeIndex !== undefined ? customizeIndex : 'group'
                    }_${postGroupIndex}`}
                    position={'right'}
                    pointer={true}
                    type={'inlined'}
                    autoZIndex={false}
                    style={{ zIndex: scssVars['z-index-overmodal'] }}>
                    {formatErrorMessages(comboErrorMessages)}
                  </Tooltip>
                </div>
              ) : null}
            </div>
          </div>
        )}
        <div className={'cell shrink'}>
          <div className={'grid-x'}>
            {warningVariationMessages.length ? (
              <div className={'cell shrink align-self-middle'}>
                <Tooltip
                  icon={'warning'}
                  target={`#tooltipTargetTextBlockWarning_${variationId}_${
                    customizeIndex !== undefined ? customizeIndex : 'group'
                  }_${postGroupIndex}`}
                  position={'right'}
                  pointer={true}
                  type={'inlined'}
                  autoZIndex={false}
                  style={{ zIndex: scssVars['z-index-overmodal'] }}>
                  {formatErrorMessages(warningVariationMessages)}
                </Tooltip>
              </div>
            ) : null}
            {errorVariationMessages.length ? (
              <div className={'cell shrink align-self-middle'}>
                <Tooltip
                  icon={'alert'}
                  target={`#tooltipTargetTextBlockErrorVariation_${variationId}_${
                    customizeIndex !== undefined ? customizeIndex : 'group'
                  }_${postGroupIndex}`}
                  position={'right'}
                  pointer={true}
                  type={'inlined'}
                  autoZIndex={false}
                  style={{ zIndex: scssVars['z-index-overmodal'] }}>
                  {formatErrorMessages(errorVariationMessages)}
                </Tooltip>
              </div>
            ) : null}
            {publishNext && breakpoint !== 'small' && breakpoint !== 'tabletportrait' ? (
              <div className={'cell shrink'}>
                <div className={classes.PublishNext}>publish next</div>
              </div>
            ) : null}
            {typeof hashtagsCount === 'number' ? (
              <div className={'cell shrink'}>
                <HashtagCounter hashtagLimit={30} hashtagsCount={hashtagsCount} />
              </div>
            ) : null}
            <div className={'cell shrink'}>
              <CharacterCounter {...(typeof variationId === 'number' ? { variationId } : {})} />
            </div>
            {!!openAiAssistant ? (
              <div className={'cell shrink'} id={`aiAssistantTooltip${idSuffix}`}>
                <Button
                  action={() => openAiAssistant()}
                  type={'button'}
                  variant={'clear'}
                  size={'medium'}
                  color={ButtonColorsEnum.primary}
                  icon={{ side: 'center', value: IconsEnum.aiAssistant }}
                />
                {aiAssistantTooltip && (
                  <Tooltip
                    type={'regular'}
                    target={`#aiAssistantTooltip${idSuffix}`}
                    autoZIndex={false}
                    style={{ zIndex: scssVars['z-index-overmodal'] }}>
                    {aiAssistantTooltip}
                  </Tooltip>
                )}
              </div>
            ) : null}
            <div className={'cell shrink'}>
              <Button
                action={() => openHashtag()}
                size={'medium'}
                color={ButtonColorsEnum.primary}
                type={'button'}
                icon={{
                  side: 'center',
                  value: IconsEnum.hashtag
                }}
                variant={'clear'}
              />
            </div>
            <div id={`snippetsTooltip${idSuffix}`} className={'cell shrink'}>
              <Button
                action={() => toggleSnippets()}
                size={'medium'}
                color={ButtonColorsEnum.primary}
                type={'button'}
                icon={{
                  side: 'center',
                  value: IconsEnum.snippet
                }}
                variant={'clear'}
              />
              {snippetsTooltip && (
                <Tooltip
                  pointer
                  type={'regular'}
                  target={`#snippetsTooltip${idSuffix}`}
                  autoZIndex={false}
                  style={{ zIndex: scssVars['z-index-overmodal'] }}>
                  {snippetsTooltip}
                </Tooltip>
              )}
            </div>
            <div id={`emojiTooltip${idSuffix}`} className={'cell shrink'}>
              {emojiTooltip && (
                <Tooltip
                  pointer
                  type={'regular'}
                  target={`#emojiTooltip${idSuffix}`}
                  autoZIndex={false}
                  style={{ zIndex: scssVars['z-index-overmodal'] }}>
                  {emojiTooltip}
                </Tooltip>
              )}
              <OutsideClickWrapper
                onClickOutside={closeEmojiHandler}
                onFocusOutside={closeEmojiHandler}
                ref={emojiWrapperRef}>
                <Button
                  action={e => {
                    e.stopPropagation();
                    setShowEmoji(old => !old);
                  }}
                  className={classes.EmojiIcon}
                  icon={{ value: IconsEnum.emoji, side: 'center' }}
                  size={'medium'}
                  color={ButtonColorsEnum.primary}
                  type={'button'}
                  variant={'clear'}
                />
                {/*showEmoji, emojiWrapperRef, portalElementRef, 'right'*/}
                <DropdownWrapper
                  className={classes.EmojiWrapper}
                  expanded={showEmoji}
                  portalWrapperRef={emojiWrapperRef}
                  side={'right'}>
                  <EmojiPicker
                    editor={editor}
                    theme={'light'}
                    onEmojiSelect={addEmoji}
                    closeEmoji={closeEmojiHandler}
                  />
                </DropdownWrapper>
              </OutsideClickWrapper>
            </div>
          </div>
        </div>
      </div>
      {showSnippet && (
        <SnippetPicker
          onToggleCreate={() => setShowCreateSnippet(old => !old)}
          editor={editor}
          onCloseSnippet={() => {
            setShowCreateSnippet(false);
            setShowSnippet(false);
          }}></SnippetPicker>
      )}
      {showCreateSnippet && <CreateEditSnippetModal onClose={() => setShowCreateSnippet(false)} mode={'create'} />}
    </div>
  );
};
